import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  AlertTriangleIcon,
  CheckCircle2Icon,
  DownloadIcon,
  FileTextIcon,
  Loader2Icon,
  Trash2Icon,
  UploadCloudIcon,
  WandIcon,
  XIcon,
} from '../components/icons.js';
import type { ExtractorRun, ExtractorVendor } from '@kennisbank/shared';
import { ApiError, apiDelete, apiGet, apiPost } from '../api/client.js';
import { Layout } from '../components/Layout.js';
import { setVisibilityAwareInterval } from '../lib/visibility-aware-interval.js';

/**
 * Data Extractor list — team-shared runs scoped to the caller tenant. The
 * operator picks a vendor invoice (PDF or xlsx), uploads it, and the
 * Cloud Run Job extracts a 36-col GncWanBlFl CSV.
 *
 * RBAC: every authenticated knowledgebase role can upload, run, refine and
 * download (`roleCanUseExtractor`, enforced at the backend). Editing the
 * shared templates is kb_admin-only.
 *
 * Poll cadence: 2.5s while any row is in {pending, extracting, validating}.
 * Mirrors AdminCodeSources' visibility-aware pattern so backgrounded tabs
 * don't ratelimit-storm the backend.
 */

const PDF_MIME = 'application/pdf';
const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const ACCEPT = '.pdf,.xlsx';
const MAX_BYTES = 500 * 1024 * 1024; // 500 MB
const MAX_BULK_FILES = 10; // per multi-file upload

type ListedRun = ExtractorRun & {
  ownerDisplayName?: string;
  outputGcsPaths?: { csv?: string; xlsx?: string; json?: string };
};

// One neutral chip for every vendor. The v1 per-brand palette used SEMANTIC
// tones decoratively (Vodacom = error-red beside FAILED-red pills, Paratus =
// warning-amber) — DESIGN.md: colour is semantic, never decorative. The
// vendor NAME is the signal; the chip just groups it visually.
const VENDOR_CHIP_NEUTRAL = 'bg-nebula-tint text-nebula-secondary';
const VENDOR_CHIP_UNKNOWN = 'bg-nebula-gray-5 text-nebula-fg-2';
function vendorChipClass(v: ExtractorVendor | undefined): string {
  if (!v || v === 'unknown') return VENDOR_CHIP_UNKNOWN;
  return VENDOR_CHIP_NEUTRAL;
}

function isInFlight(s: ExtractorRun['status']): boolean {
  return s === 'pending' || s === 'extracting' || s === 'validating';
}

function statusPill(status: ExtractorRun['status']): JSX.Element {
  const styles: Record<ExtractorRun['status'], string> = {
    pending: 'bg-nebula-tint text-nebula-secondary',
    extracting: 'bg-nebula-tint text-nebula-secondary',
    validating: 'bg-nebula-tint text-nebula-secondary',
    ready: 'bg-nebula-success-bg text-nebula-success',
    partial: 'bg-nebula-warning-bg text-nebula-warning',
    failed: 'bg-nebula-error-bg text-nebula-error',
  };
  const labels: Record<ExtractorRun['status'], string> = {
    pending: 'Queued',
    extracting: 'Extracting',
    validating: 'Validating',
    ready: 'Ready',
    partial: 'Partial',
    failed: 'Failed',
  };
  return (
    <span
      className={`ov-meta-upper inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 ${styles[status]}`}
    >
      {isInFlight(status) && (
        <span className="inline-flex h-1.5 w-1.5 animate-pulse rounded-full bg-nebula-secondary" />
      )}
      {labels[status]}
    </span>
  );
}

function reconciliationChip(run: ListedRun): JSX.Element | null {
  if (run.status !== 'ready' && run.status !== 'partial') return null;
  const r = run.grandTotalReconciliation;
  if (r === 'match') {
    return (
      <span
        title="Extracted line totals match the document's stated grand total."
        className="inline-flex items-center gap-1 rounded-md bg-nebula-success-bg px-1.5 py-0.5 text-[10px] font-medium text-nebula-success"
      >
        <CheckCircle2Icon size={11} />
        Match
      </span>
    );
  }
  if (r === 'mismatch') {
    return (
      <span
        title="Extracted line totals differ from the document's stated grand total. Operator review required."
        className="inline-flex items-center gap-1 rounded-md bg-nebula-warning-bg px-1.5 py-0.5 text-[10px] font-medium text-nebula-warning"
      >
        <AlertTriangleIcon size={11} />
        Mismatch
      </span>
    );
  }
  // Recipe-driven templates have no external grand total to reconcile against,
  // so "unknown" is the normal state for them — a grey "Unknown" chip reads as
  // a problem. Render a quiet dash instead.
  return (
    <span
      title="No external grand total to reconcile against for this template."
      className="text-nebula-fg-4"
    >
      —
    </span>
  );
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

function fmtBytes(n: number): string {
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function fmtCost(n: number | undefined): string {
  if (!n || n <= 0) return '—';
  return `$${n.toFixed(2)}`;
}

export function ExtractorPage(): JSX.Element {
  const [runs, setRuns] = useState<ListedRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  // Combined-CSV export: which runs are ticked, and whether we're building it.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [combining, setCombining] = useState(false);
  // templateId → display name, so each run can show its format + we can gate
  // combined selection to one template.
  const [templateNames, setTemplateNames] = useState<Record<string, string>>({});

  const downloadable = (r: ListedRun): boolean => r.status === 'ready' || r.status === 'partial';
  const toggleSelect = (id: string): void =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Combine the selected runs into ONE CSV. Streams from the backend as a
  // blob (not a signed URL — it's assembled on the fly), so we trigger the
  // download client-side. Reconciliation mismatches prompt for a justification.
  const downloadCombined = useCallback(
    async (justification?: string): Promise<void> => {
      const runIds = [...selectedIds];
      if (runIds.length === 0) return;
      setCombining(true);
      try {
        const res = await fetch('/api/v1/extractor/runs/download-combined', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            runIds,
            ...(justification ? { downloadJustification: justification } : {}),
          }),
        });
        if (!res.ok) {
          const body = (await res.json().catch(() => ({}))) as { error?: string; message?: string };
          if (body.error === 'justification_required') {
            const j = window.prompt(
              'One or more selected runs are a reconciliation mismatch. Add a short justification to download:',
              '',
            );
            if (!j || !j.trim()) return;
            return downloadCombined(j.trim());
          }
          throw new Error(body.message || 'Combined export failed.');
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'combined-extract.csv';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Combined export failed.');
      } finally {
        setCombining(false);
      }
    },
    [selectedIds],
  );

  const refresh = useCallback(async () => {
    try {
      const res = await apiGet<{ runs: ListedRun[] }>('/api/v1/extractor/runs');
      setRuns(res.runs ?? []);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load extractor runs.');
    }
  }, []);

  // Template names for the "format" label + the same-template selection gate.
  useEffect(() => {
    apiGet<{ templates: { id: string; displayName: string }[] }>('/api/v1/extractor/templates')
      .then((r) => {
        const m: Record<string, string> = {};
        for (const t of r.templates ?? []) m[t.id] = t.displayName;
        setTemplateNames(m);
      })
      .catch(() => undefined);
  }, []);

  const templateLabel = (r: ListedRun): string =>
    templateNames[r.templateId] ?? r.templateSnapshot?.outputShape ?? r.vendor ?? 'Template';
  // Combined export needs one template. Once a run is ticked, only runs from
  // the SAME template can join — the rest are disabled so the mismatch never
  // reaches the server.
  const selectedTemplateId =
    selectedIds.size > 0 ? (runs.find((r) => selectedIds.has(r.id))?.templateId ?? null) : null;
  const canCombineWith = (r: ListedRun): boolean =>
    selectedTemplateId === null || r.templateId === selectedTemplateId;

  useEffect(() => {
    setLoading(true);
    void refresh().finally(() => setLoading(false));
  }, [refresh]);

  // Poll while anything is in-flight (2.5s cadence, visibility-aware).
  useEffect(() => {
    const hasInFlight = runs.some((r) => isInFlight(r.status));
    if (!hasInFlight) return;
    return setVisibilityAwareInterval(() => {
      void refresh();
    }, 2500);
  }, [runs, refresh]);

  const onDelete = async (runId: string, sourceFileName: string): Promise<void> => {
    if (
      !window.confirm(
        `Delete the run for "${sourceFileName}"? Child refinements will be tombstoned too.`,
      )
    )
      return;
    try {
      await apiDelete(`/api/v1/extractor/runs/${runId}`);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-6 py-8">
        <header className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-nebula-fg-1">Data Extractor</h1>
            <p className="mt-1 text-sm text-nebula-fg-2">
              Upload a vendor file and pick its import template. The pipeline extracts the line
              items and reshapes them into that template&rsquo;s OneView import format, ready to
              download and import.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="inline-flex shrink-0 items-center gap-2 rounded-md bg-nebula-primary px-4 py-2 text-sm font-medium text-white hover:bg-nebula-secondary"
          >
            <UploadCloudIcon size={16} />
            Upload invoices
          </button>
        </header>

        {error && (
          <p
            role="alert"
            className="mb-4 rounded-md bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error"
          >
            {error}
          </p>
        )}

        {runs.length === 0 && !loading && <EmptyState onUpload={() => setUploadOpen(true)} />}

        {runs.length > 0 && (
          <div className="overflow-hidden rounded-lg border border-nebula-border-soft bg-nebula-surface">
            {selectedIds.size > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-nebula-border-soft bg-nebula-tint px-3 py-2">
                <span className="text-sm font-medium text-nebula-fg-1">
                  {selectedIds.size} run{selectedIds.size === 1 ? '' : 's'} selected
                  {selectedTemplateId && (
                    <span className="ml-1.5 font-normal text-nebula-fg-3">
                      · {templateNames[selectedTemplateId] ?? 'same template'} · only matching runs
                      stay selectable
                    </span>
                  )}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSelectedIds(new Set())}
                    className="rounded border border-nebula-border bg-nebula-surface px-2 py-1 text-xs text-nebula-fg-2 hover:bg-nebula-tint"
                  >
                    Clear
                  </button>
                  <button
                    type="button"
                    onClick={() => void downloadCombined()}
                    disabled={combining}
                    className="inline-flex items-center gap-1.5 rounded-md bg-nebula-primary px-3 py-1 text-xs font-medium text-white hover:bg-nebula-secondary disabled:opacity-50"
                  >
                    {combining ? (
                      <Loader2Icon size={13} className="animate-spin" />
                    ) : (
                      <DownloadIcon size={13} />
                    )}
                    Download {selectedIds.size} as one CSV
                  </button>
                </div>
              </div>
            )}
            <table className="w-full text-sm">
              <thead className="border-b border-nebula-border-soft bg-nebula-gray-6">
                <tr>
                  <th className="w-8 px-3 py-2" aria-label="Select" />
                  <th className="ov-meta-upper px-3 py-2 text-left">Source</th>
                  <th className="ov-meta-upper px-3 py-2 text-left">Vendor</th>
                  <th className="ov-meta-upper px-3 py-2 text-left">Status</th>
                  <th className="ov-meta-upper px-3 py-2 text-left">Reconciliation</th>
                  <th className="ov-meta-upper px-3 py-2 text-right">Cost</th>
                  <th className="ov-meta-upper px-3 py-2 text-left">Created</th>
                  <th className="ov-meta-upper px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-nebula-border-soft hover:bg-nebula-tint"
                  >
                    <td className="px-3 py-2 align-top">
                      {downloadable(r) && (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.id)}
                          disabled={!selectedIds.has(r.id) && !canCombineWith(r)}
                          onChange={() => toggleSelect(r.id)}
                          aria-label={`Select ${r.sourceFileName} for combined download`}
                          title={
                            !selectedIds.has(r.id) && !canCombineWith(r)
                              ? 'Different template — clear the selection to combine these instead'
                              : 'Select for a combined CSV download'
                          }
                          className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-nebula-primary disabled:cursor-not-allowed disabled:opacity-30"
                        />
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">
                      <Link
                        to={`/workspace/extractor/runs/${r.id}`}
                        className="inline-flex items-start gap-2 text-nebula-fg-1 hover:text-nebula-secondary hover:underline"
                      >
                        <FileTextIcon
                          size={14}
                          className="mt-0.5 flex-shrink-0 text-nebula-fg-4"
                          aria-hidden="true"
                        />
                        <span className="break-all">{r.sourceFileName}</span>
                      </Link>
                      <div className="ml-5 text-[11px] text-nebula-fg-3">
                        {fmtBytes(r.sourceBytes)} · {r.sourceMime === XLSX_MIME ? 'XLSX' : 'PDF'}
                        <span
                          className="ml-1.5 inline-flex items-center rounded bg-nebula-gray-5 px-1.5 py-0.5 text-[10px] font-medium text-nebula-fg-2"
                          title="Template / format — only runs sharing this can be combined into one CSV"
                        >
                          {templateLabel(r)}
                        </span>
                        {r.parentRunId && (
                          <span className="ml-1.5 inline-flex items-center gap-1 text-nebula-secondary">
                            <WandIcon size={11} />
                            refined
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top">
                      <span
                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${vendorChipClass(
                          r.vendor,
                        )}`}
                      >
                        {r.vendor}
                      </span>
                    </td>
                    <td className="px-3 py-2 align-top">
                      {statusPill(r.status)}
                      {/* Surface the failure reason in the list — before this,
                          a Failed run gave zero clue without opening it. */}
                      {r.status === 'failed' && r.errorMessage && (
                        <span
                          className="mt-1 block max-w-[14rem] truncate text-[11px] text-nebula-error"
                          title={r.errorMessage}
                        >
                          {r.errorMessage}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top">{reconciliationChip(r)}</td>
                    <td className="px-3 py-2 text-right align-top font-mono text-[12px] text-nebula-fg-2">
                      {fmtCost(r.costUsd)}
                    </td>
                    <td className="px-3 py-2 align-top text-[12px] text-nebula-fg-3">
                      <div>{formatDate(r.createdAt)}</div>
                      <div className="text-[11px] text-nebula-fg-4">
                        by {r.ownerDisplayName ?? r.ownerUserId}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right align-top">
                      <div className="inline-flex items-center gap-1">
                        <Link
                          to={`/workspace/extractor/runs/${r.id}`}
                          className="rounded border border-nebula-border bg-nebula-surface px-2 py-1 text-xs text-nebula-fg-1 hover:bg-nebula-tint"
                        >
                          View
                        </Link>
                        {(r.status === 'ready' || r.status === 'partial') && (
                          <DownloadButton run={r} onError={(m) => setError(m)} />
                        )}
                        <button
                          type="button"
                          onClick={() => void onDelete(r.id, r.sourceFileName)}
                          aria-label={`Delete ${r.sourceFileName}`}
                          className="rounded border border-nebula-border bg-nebula-surface p-1 text-nebula-fg-3 hover:bg-nebula-error-bg hover:text-nebula-error"
                        >
                          <Trash2Icon size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {loading && runs.length === 0 && (
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-nebula-fg-3">
            <Loader2Icon size={14} className="animate-spin" />
            Loading runs…
          </div>
        )}
      </div>

      {uploadOpen && (
        <UploadModal
          onClose={() => setUploadOpen(false)}
          onUploaded={() => {
            setUploadOpen(false);
            void refresh();
          }}
        />
      )}
    </Layout>
  );
}

function EmptyState({ onUpload }: { onUpload: () => void }): JSX.Element {
  return (
    <div className="rounded-lg border-2 border-dashed border-nebula-border bg-nebula-surface p-10 text-center">
      <UploadCloudIcon size={28} className="mx-auto text-nebula-fg-4" />
      <h2 className="mt-3 text-base font-semibold text-nebula-fg-1">No extractor runs yet</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-nebula-fg-2">
        Upload a vendor file and pick its import template. The pipeline extracts the line items into
        that template&rsquo;s OneView import format, with a row preview and a one-click CSV / XLSX
        download.
      </p>
      <button
        type="button"
        onClick={onUpload}
        className="mt-4 inline-flex items-center gap-2 rounded-md bg-nebula-primary px-4 py-2 text-sm font-medium text-white hover:bg-nebula-secondary"
      >
        <UploadCloudIcon size={16} />
        Upload first invoice
      </button>
    </div>
  );
}

function DownloadButton({
  run,
  onError,
}: {
  run: ListedRun;
  onError: (msg: string) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const isMismatch = run.grandTotalReconciliation === 'mismatch';

  const onClick = async (): Promise<void> => {
    let justification: string | undefined;
    if (isMismatch) {
      const v = window.prompt(
        'Reconciliation is a mismatch. Add a short justification (required) to download the CSV:',
        '',
      );
      if (!v || !v.trim()) return;
      justification = v.trim();
    }
    setBusy(true);
    try {
      const qs = justification ? `?downloadJustification=${encodeURIComponent(justification)}` : '';
      const res = await apiGet<{ url: string }>(
        `/api/v1/extractor/runs/${run.id}/download/csv${qs}`,
      );
      window.location.href = res.url;
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Download failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={busy}
      aria-label={`Download CSV for ${run.sourceFileName}`}
      className="inline-flex items-center gap-1 rounded border border-nebula-border bg-nebula-surface px-2 py-1 text-xs text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-50"
    >
      <DownloadIcon size={12} />
      CSV
    </button>
  );
}

// ----- Upload modal --------------------------------------------------------

interface ActiveTemplate {
  id: string;
  displayName: string;
}

function UploadModal({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [phase, setPhase] = useState<'idle' | 'minting' | 'uploading' | 'starting'>('idle');
  // Bulk progress: which file of how many is currently processing.
  const [bulk, setBulk] = useState<{ index: number; total: number; name: string } | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [templates, setTemplates] = useState<ActiveTemplate[]>([]);
  const [templateId, setTemplateId] = useState<string>('');

  // Load active templates. Backend bootstraps `tmpl_gnc_wan` on first call
  // so we never see an empty list under normal operation.
  useEffect(() => {
    let cancelled = false;
    apiGet<{ templates: ActiveTemplate[] }>('/api/v1/extractor/templates?status=active')
      .then((res) => {
        if (cancelled) return;
        const list = res.templates ?? [];
        setTemplates(list);
        if (list.length > 0) setTemplateId(list[0].id);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiError ? err.message : 'Could not load templates.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const pick = (incoming: File[]): void => {
    setError(null);
    const ok: File[] = [];
    for (const f of incoming) {
      if (f.type !== PDF_MIME && f.type !== XLSX_MIME) {
        setError(`Skipped "${f.name}" — unsupported type. Use PDF or XLSX.`);
        continue;
      }
      if (f.size > MAX_BYTES) {
        setError(`Skipped "${f.name}" — larger than 500 MB.`);
        continue;
      }
      ok.push(f);
    }
    setFiles((prev) => {
      const merged = [...prev];
      for (const f of ok) {
        if (!merged.some((m) => m.name === f.name && m.size === f.size)) merged.push(f);
      }
      if (merged.length > MAX_BULK_FILES) {
        setError(`Up to ${MAX_BULK_FILES} files at a time.`);
        return merged.slice(0, MAX_BULK_FILES);
      }
      return merged;
    });
  };

  const putToGcs = (uploadUrl: string, f: File): Promise<void> =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', f.type);
      // The signed URL enforces x-goog-content-length-range.
      xhr.setRequestHeader('x-goog-content-length-range', `1,${MAX_BYTES}`);
      xhr.upload.onprogress = (evt) => {
        if (evt.lengthComputable) setProgress(evt.loaded / evt.total);
      };
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300
          ? resolve()
          : reject(new Error(`upload failed: ${xhr.status}`));
      xhr.onerror = () => reject(new Error('upload network error'));
      xhr.send(f);
    });

  // The extractor holds a per-tenant lock (one run at a time). Retry start on
  // a busy lock, then poll this run to a terminal state so the caller can move
  // to the next file without a 409.
  const startAndWait = async (runId: string): Promise<void> => {
    for (;;) {
      try {
        await apiPost(`/api/v1/extractor/runs/${runId}/start`, {});
        break;
      } catch (err) {
        if (err instanceof ApiError && err.code === 'extractor_busy') {
          await new Promise((r) => setTimeout(r, 3000));
          continue;
        }
        throw err;
      }
    }
    for (;;) {
      await new Promise((r) => setTimeout(r, 3000));
      const s = await apiGet<{ status?: string }>(`/api/v1/extractor/runs/${runId}`);
      if (s.status === 'ready' || s.status === 'partial' || s.status === 'failed') return;
    }
  };

  const submit = async (): Promise<void> => {
    if (files.length === 0 || !templateId) return;
    setError(null);
    // One batchId across a multi-file upload so the whole set can be selected
    // + downloaded as a single combined CSV.
    const batchId =
      files.length > 1
        ? `batch_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
        : undefined;
    let firstRunId: string | null = null;
    try {
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        setBulk({ index: i, total: files.length, name: f.name });
        setProgress(null);
        setPhase('minting');
        const mint = await apiPost<{ runId: string; uploadUrl: string }>(
          '/api/v1/extractor/runs/upload-url',
          {
            sourceFileName: f.name,
            mimeType: f.type,
            sizeBytes: f.size,
            templateId,
            ...(batchId ? { batchId } : {}),
          },
        );
        if (!firstRunId) firstRunId = mint.runId;
        setPhase('uploading');
        await putToGcs(mint.uploadUrl, f);
        setPhase('starting');
        await startAndWait(mint.runId);
      }
      onUploaded();
      if (files.length === 1 && firstRunId) {
        navigate(`/workspace/extractor/runs/${firstRunId}`);
      } else {
        onClose();
      }
    } catch (err) {
      setPhase('idle');
      setProgress(null);
      setBulk(null);
      if (err instanceof ApiError && err.code === 'extractor_busy') {
        setError('Another extractor run is in progress. Wait for it to finish, then retry.');
      } else {
        setError(err instanceof Error ? err.message : 'Upload failed.');
      }
    }
  };

  const busy = phase !== 'idle';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={busy ? undefined : onClose}
    >
      <div
        className="w-full max-w-lg rounded-lg bg-nebula-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-nebula-border-soft px-4 py-3">
          <h2 className="text-base font-semibold text-nebula-fg-1">Upload invoices</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close upload"
            className="rounded-md p-1.5 text-nebula-fg-3 hover:bg-nebula-tint hover:text-nebula-fg-1 disabled:opacity-40"
          >
            <XIcon size={16} />
          </button>
        </div>

        <div className="space-y-4 p-4">
          <div
            className="rounded-lg border-2 border-dashed border-nebula-border bg-nebula-tint p-6 text-center"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              if (busy) return;
              pick(Array.from(e.dataTransfer.files ?? []));
            }}
          >
            <input
              ref={fileRef}
              type="file"
              accept={ACCEPT}
              multiple
              hidden
              onChange={(e) => {
                pick(Array.from(e.target.files ?? []));
                e.target.value = '';
              }}
            />
            {files.length === 0 ? (
              <>
                <UploadCloudIcon size={24} className="mx-auto text-nebula-fg-4" />
                <p className="mt-2 text-sm text-nebula-fg-1">
                  Drop PDFs / XLSX here, or choose files. Up to {MAX_BULK_FILES} at once — they run
                  one after another and can be downloaded together as a single CSV.
                </p>
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={busy}
                  className="mt-3 rounded-md bg-nebula-primary px-3 py-1.5 text-sm font-medium text-white hover:bg-nebula-secondary disabled:opacity-50"
                >
                  Choose files
                </button>
                <p className="mt-2 text-[11px] text-nebula-fg-3">Up to 500 MB each.</p>
              </>
            ) : (
              <div className="text-left">
                <ul className="space-y-1.5">
                  {files.map((f, idx) => (
                    <li key={`${f.name}-${f.size}`} className="flex items-start gap-2">
                      <FileTextIcon size={16} className="mt-0.5 flex-shrink-0 text-nebula-fg-4" />
                      <div className="min-w-0 flex-1">
                        <p className="break-all text-sm text-nebula-fg-1">{f.name}</p>
                        <p className="text-[11px] text-nebula-fg-3">
                          {fmtBytes(f.size)} · {f.type === XLSX_MIME ? 'XLSX' : 'PDF'}
                          {bulk && bulk.index === idx && ' · processing…'}
                        </p>
                      </div>
                      {!busy && (
                        <button
                          type="button"
                          onClick={() => setFiles((prev) => prev.filter((_, i) => i !== idx))}
                          aria-label={`Remove ${f.name}`}
                          className="rounded-md p-1 text-nebula-fg-3 hover:bg-nebula-surface hover:text-nebula-fg-1"
                        >
                          <XIcon size={13} />
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
                {!busy && files.length < MAX_BULK_FILES && (
                  <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    className="mt-2 text-[12px] font-medium text-nebula-secondary hover:underline"
                  >
                    + Add more files
                  </button>
                )}
                {bulk && (
                  <p className="mt-3 text-[11px] text-nebula-fg-3">
                    File {bulk.index + 1} of {bulk.total}: {bulk.name}
                    {phase === 'minting' && ' — preparing…'}
                    {phase === 'starting' && ' — extracting (this can take a bit)…'}
                  </p>
                )}
                {progress !== null && (
                  <div className="mt-2">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-nebula-gray-5">
                      <div
                        className="h-full bg-nebula-primary transition-all"
                        style={{ width: `${Math.round(progress * 100)}%` }}
                      />
                    </div>
                    <p className="mt-1 text-[11px] text-nebula-fg-3">
                      Uploading… {Math.round(progress * 100)}%
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Template picker — hidden when only one active template exists.
              Backend bootstraps tmpl_gnc_wan so the zero-template case
              shouldn't happen, but we surface a hint if it does. */}
          {templates.length > 1 && (
            <label className="block text-sm">
              <span className="ov-meta-upper mb-1 block text-nebula-fg-3">Import template</span>
              <select
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={busy}
                className="w-full rounded-md border border-nebula-border bg-nebula-surface px-3 py-2 text-sm focus:border-nebula-secondary focus:outline-none disabled:opacity-50"
              >
                {templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.displayName}
                  </option>
                ))}
              </select>
            </label>
          )}
          {templates.length === 0 && (
            <p className="rounded-md bg-nebula-warning-bg px-3 py-2 text-sm text-nebula-warning">
              No active templates. Ask an admin to create one in /admin/extractor-templates.
            </p>
          )}

          {error && (
            <p
              role="alert"
              className="rounded-md bg-nebula-error-bg px-3 py-2 text-sm text-nebula-error"
            >
              {error}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-nebula-border-soft bg-nebula-app/40 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-nebula-border bg-nebula-surface px-3 py-1.5 text-sm text-nebula-fg-1 hover:bg-nebula-tint disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={files.length === 0 || busy || !templateId}
            className="inline-flex items-center gap-2 rounded-md bg-nebula-primary px-4 py-1.5 text-sm font-medium text-white hover:bg-nebula-secondary disabled:opacity-50"
          >
            {busy && <Loader2Icon size={14} className="animate-spin" />}
            {busy && bulk
              ? `Processing ${bulk.index + 1}/${bulk.total}…`
              : files.length > 1
                ? `Extract ${files.length} files`
                : 'Start extraction'}
          </button>
        </div>
      </div>
    </div>
  );
}
